/**
 * Handoff giữa middleware và client cho location đến từ URL (WebInApp / deep link).
 *
 * Middleware là NƠI DUY NHẤT phân giải city từ `?lat&lng` (gọi `cities/by-point`
 * một lần), rồi:
 *  - ghi cookie city bền (`ahafood_city_*`) + `ahafood_address` cho case hợp lệ,
 *  - ghi cookie one-shot {@link WEBVIEW_LOCATION_COOKIE} để client biết PHẢI áp
 *    location này (ghi đè store đang persist) hay phải bung dialog chọn lại,
 *  - xoá `lat/lng/address` khỏi URL đích.
 *
 * Module này phải chạy được ở Edge runtime → chỉ chứa hằng số + hàm thuần,
 * KHÔNG import `next/headers`, React hay bất cứ thứ gì browser-only.
 */

/** Cookie one-shot middleware → client. Client đọc xong là xoá ngay. */
export const WEBVIEW_LOCATION_COOKIE = 'ahafood_wv_location';

/** Sống đủ lâu để client kịp đọc sau redirect, không đủ lâu để rò sang phiên sau. */
export const WEBVIEW_LOCATION_MAX_AGE = 60; // giây

export const CITY_COOKIE = {
  id: 'ahafood_city_id',
  slug: 'ahafood_city_slug',
  name: 'ahafood_city_name',
} as const;

export const ADDRESS_COOKIE = 'ahafood_address';

export const CITY_COOKIE_MAX_AGE = 30 * 24 * 60 * 60; // 30 ngày

/**
 * `ok` — by-point phân giải được, toạ độ nằm trong vùng phục vụ.
 * `not_serviceable` — by-point trả 404/NOT_FOUND_CITY: toạ độ ngoài mọi city.
 * `error` — lỗi mạng/parse VÀ không có cookie city để dùng tạm.
 *
 * Với `error` mà user đã có cookie city từ trước, middleware coi như `ok` và đi
 * tiếp im lặng — một blip mạng không đáng để chặn user bằng dialog.
 */
export type WebviewLocationStatus = 'ok' | 'not_serviceable' | 'error';

export interface WebviewLocationCity {
  id: string;
  slug: string;
  name: string;
}

export interface WebviewLocationPayload {
  lat: number;
  lng: number;
  /** Label địa chỉ do native truyền qua `?address` (nếu có). */
  label?: string;
  status: WebviewLocationStatus;
  /** City đã phân giải. Với status != 'ok' đây chỉ là city để render trang. */
  city?: WebviewLocationCity;
}

/**
 * Segment top-level KHÔNG phải city slug. Route `/[locale]/[city]` là catch-all
 * nên mọi segment lạ đều khớp — danh sách này để middleware biết khi nào được
 * phép thay segment đầu bằng slug vừa phân giải.
 *
 * Nguồn: `apps/home/src/app/[locale]/*` (kể cả route group) + route ngoài locale.
 */
export const RESERVED_TOP_LEVEL_SEGMENTS: ReadonlySet<string> = new Set([
  'amori',
  'api',
  'auth',
  'blog',
  'chat',
  'cua-hang',
  'm',
  'order',
  'don-hang',
  'order-wv',
  'permate-test',
  'search',
  'store',
]);

/** Param bị xoá khỏi URL đích sau khi middleware đã nuốt vào cookie. */
export const CONSUMED_LOCATION_PARAMS = ['lat', 'lng', 'address'] as const;

export function serializeWebviewLocation(
  payload: WebviewLocationPayload
): string {
  return encodeURIComponent(JSON.stringify(payload));
}

/**
 * Parse cookie one-shot. Trả `null` nếu thiếu/hỏng/toạ độ không hợp lệ — caller
 * khi đó rơi về nhánh đọc `?lat&lng` như cũ.
 */
export function parseWebviewLocation(
  raw?: string | null
): WebviewLocationPayload | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(
      decodeURIComponent(raw)
    ) as WebviewLocationPayload;
    if (!Number.isFinite(parsed?.lat) || !Number.isFinite(parsed?.lng)) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}
